<template>
  <div class="mb-1">
    <div v-if="showSpinner" class="loader-wrapper">
      <div class="loader"></div>
    </div>
    <new-vector-modal ref="vectorModal" :existingVectors="vectors" @vectorCreated="addVector" @loadVectors="loadVectors" />
    <alert ref="alert" />
    <b-row class="mt-2 mb-2">
      <b-col class="text-left">
        <h3>Vectors</h3>
      </b-col>
      <b-col class="text-right">
        <b-btn variant="info" @click="showNewVectorModal" class="text-nowrap mr-1">Add vector</b-btn>
        <b-btn variant="info" @click="saveVectors" class="text-nowrap mr-1">Save</b-btn>
      </b-col>
    </b-row>
    <b-row class="flex-nowrap">
      <b-col v-for="(v, vectorId) in vectors" :key=v.value style="min-width: 180px; max-width: 150px;">
        <table class="table table-sm table-hover">
          <thead class="vector-header">
            <th colspan="2">
              <b-form-input size=sm class="vector-header" :value="v.Name" @change="(e) => { v.Name = e; vectorsChanged() }"></b-form-input>
              <b-button @click="removeVector(vectorId)" class="rounded-button">&times;</b-button>
              <!-- <md-button @click="removeVector(vectorId)" class="md-fab md-primary remove">&times;</md-button> -->
            </th>
          </thead>
          <tbody>
            <tr v-if="isAdmin">
              <td colspan="2" class="pt-1 pb-1" >
                <b-form-checkbox v-model="v.Shared" @change="(e) => {v.Shared = e; vectorsChanged()}">
                  Shared
                </b-form-checkbox>
              </td>
            </tr>
            <tr v-for="(val, index) in v.Value" :key=index>
              <td>
                {{index + 1}}
              </td>
              <td>
                <b-form-input :ref="vectorId" @keydown.tab.native="handleTabPress($event,v, v.Value.length-1 == index, vectorId)"
                  @change="(e) => {v.Value[index] = e; vectorsChanged()}" size="sm" :value="val"></b-form-input>
              </td>
            </tr>
            <tr class="tr-buttons">
              <td class="td-button" v-on:click="addPeriod(v)">
                <b-btn style="width: 100%; height: 100%; text-decoration: none;" size="sm" variant="link">+</b-btn>
              </td>
              <td class="td-button" v-if="v.Value.length > 0" v-on:click="removePeriod(v)">
                <b-btn style="width: 100%; height: 100%; text-decoration: none;" size="sm" variant="link">-</b-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import service from '../../services/HttpRequestService';
import Alert from '../Alert';
import NewVectorModal from './NewVectorModal';
import { isNumericArrayValid } from '../../utilities/ArrayUtils';
import { isNotEmptyArray } from '../../utilities/ArrayUtils';
import * as XLSX from 'xlsx';
import { TRACE_ERROR } from '../../constants';

export default {
  data() {
    return {
      vectors: [],
      alert: {
        timestamp: 0,
        type: '',
        msg: ''
      },
      showSpinner: false,
      unsavedChanges: false
    };
  },
  async created() {
    this.showSpinner = true;
    await this.fetchVectors();
    this.showSpinner = false;
  },
  methods: {
    async fetchVectors() {
      try {
        let res = await service.getUserVectors();
        if (res.status == 200) {
          this.vectors = res.data;
        }
      } catch (err) {
        await service.log(TRACE_ERROR, err.response.data, err.stack);
        this.$refs.alert.showAlert('danger', err.response.data);
      }
    },
    handleTabPress(event, vector, isLastRow, vectorId) {
      if (!event.shiftKey) {
        this.addPeriodIfNeeded(vector, isLastRow, vectorId);
        this.vectorsChanged();
      }
    },
    removePeriod(vector) {
      vector.Value.pop();
      this.vectorsChanged();
    },
    addPeriod(vector) {
      vector.Value.push('');
      this.vectorsChanged();
    },
    addPeriodIfNeeded(vector, isLastRow, vectorId) {
      if (isLastRow) {
        this.addPeriod(vector);
        this.$forceUpdate();
        this.$nextTick(function() {
          this.$refs[vectorId][vector.Value.length - 1].$el.focus();
        });
      }
    },
    addVector(vector = {}) {
      if (vector == {} || !isNotEmptyArray(vector.Value)) {
        this.vectors.unshift({
          Name: vector.Name,
          Value: Array.apply('', Array(360)).map(function() {}),
          Shared: vector.Shared
        });
      } else {
        populateEmptyCells(vector);
        this.vectors.unshift(vector);
      }
      this.vectorsChanged();
    },
    removeVector(vectorIndex) {
      this.vectors.splice(vectorIndex, 1);
      this.vectorsChanged();
    },
    async saveVectors() {
      let valid = true;
      for (let vector of this.vectors) {
        populateEmptyCells(vector.Value);
        if (
          !(
            isNotEmptyArray(vector.Value) &&
            isNumericArrayValid(vector.Value) &&
            vector.Name.length > 0
          )
        ) {
          valid = false;
          break;
        }
      }
      if (valid) {
        try {
          let res = await service.saveVectors(this.vectors);
          this.$refs.alert.showAlert('success', 'Sucessfully saved vectors');
          this.unsavedChanges = false;
          return true;
        } catch (err) {
          await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
          this.$refs.alert.showAlert('danger', err.response.data.Message);
        }
      } else {
        this.$refs.alert.showAlert('danger', 'Empty or incorrect vector.');
      }
      return false;
    },
    showNewVectorModal(user) {
      this.$refs.vectorModal.showModal();
    },
    loadVectors(file) {
      const reader = new FileReader();
      reader.onload = e => {
        const bstr = e.target.result;
        try {
          this.showSpinner = true;
          const wb = XLSX.read(bstr, { type: 'binary' });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

          if (!Array.isArray(data) || !data.length) {
            this.$refs.alert.showAlert('danger', 'No data in ' + file.name + ' file found.');
            return;
          }
          for (let x = 1; x < data[0].length; x++) {
            let vector = {};
            vector.Name = data[0][x];
            let values = [];
            for (let y = 1; y < data.length; y++) {
              values.push(data[y][x]);
            }
            vector.Value = values;

            this.addVector(vector);
          }
        } catch (err) {
          this.$refs.alert.showAlert('danger', err.message);
        } finally {
          this.showSpinner = false;
          this.$refs.vectorModal.hide();
        }
      };
      reader.readAsBinaryString(file);
    },
    vectorsChanged() {
      this.unsavedChanges = true;
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    }
  },
  components: {
    Alert,
    NewVectorModal
  },
  async beforeRouteLeave(to, from, next) {
    let proceedRouting = true;
    if (this.unsavedChanges) {
      const shouldSave = window.confirm('You have unsaved changes! Do you want to save them?');
      if (shouldSave) {
        proceedRouting = await this.saveVectors();
      }
    }
    next(proceedRouting);
  }
};
function populateEmptyCells(vector) {
  if (vector[0] == undefined) {
    vector[0] = 0;
  }
  for (let i = 0; i < vector.length; i++) {
    if (vector[i] === undefined) {
      vector[i] = vector[i - 1];
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .table th,
::v-deep .table td {
  border-left: 1px solid #cacaca;
  border-top: 1px solid #cacaca;
  border-bottom: 1px solid #cacaca;
  border-right: 1px solid #cacaca;
}
::v-deep .form-control-sm {
  border: none;
  text-align: right;
}
::v-deep .vector-header {
  caption-side: top;
  text-align: center;
  padding: 0 0;
  color: #fff;
  background-color: #17a2b8;
  &:hover {
    text-decoration: underline;
  }
}
.tr-buttons:hover {
  background-color: #fff;
}
.td-button:hover {
  background-color: #ebebeb;
}
.md-button {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(-30%, -40%);
}
.btn-add-vector {
  position: relative;
  display: inline-block;
}
</style>
